import { motion } from "framer-motion";
import { useT } from "../lang";

const T = {
  pt: {
    label: "02 · sobre",
    heading1: "PRAZER,",
    heading2: "GABI.",
    p1a: "Criativa multidisciplinar com ",
    p1b: "dez anos de estrada",
    p1c: " entre agências, redações e internet.",
    pManifesto: ["Escrevo. Crio. Comunico.", "Penso estratégia. Construo comunidade."],
    p2:
      "Construí minha carreira no Brasil e, há três anos, vivo entre o Brasil e o Brooklyn. Hoje transformo esse olhar entre culturas em estratégias que conectam marcas às conversas certas.",
    p3:
      "Já desenvolvi conteúdo, campanhas e estratégias criativas para marcas de diferentes segmentos, sempre combinando repertório, contexto e comportamento digital.",
    p4a: "Acredito que as melhores ideias nascem da escuta. E que toda boa estratégia começa entendendo ",
    p4b: "pessoas, não algoritmos",
    caption1: "✷ new york · 2025",
    photoAlt: "Gabriela Ferraz em Nova York",
    services: [
      {
        t: "estratégia",
        items: ["Estratégia criativa", "Estratégia de conteúdo", "Posicionamento & tom de voz", "Planejamento editorial"],
      },
      { t: "redação", items: ["Copywriting", "Storytelling", "Roteiros", "Manifesto de marca"] },
      { t: "social", items: ["Community management", "Gestão de influenciadores", "Cobertura em tempo real"] },
      { t: "editorial", items: ["Pauta & apuração", "Edição de conteúdo", "Podcasts", "Reels & TikTok"] },
      { t: "campanhas", items: ["Conceito criativo", "Lançamento", "Ativação digital"] },
    ],
  },
  en: {
    label: "02 · about",
    heading1: "NICE",
    heading2: "TO MEET YOU.",
    p1a: "Multidisciplinary creative with ",
    p1b: "ten years on the road",
    p1c: " between agencies, newsrooms, and the internet.",
    pManifesto: ["I write. I create. I communicate.", "I think strategy. I build community."],
    p2:
      "I built my career in Brazil and, for the past three years, I've been living between Brazil and Brooklyn. I turn this cross-cultural lens into strategies that connect brands to the right conversations.",
    p3:
      "I've developed content, campaigns, and creative strategies for brands across many industries — always mixing culture, context, and digital behavior.",
    p4a: "I believe the best ideas come from listening. And that every good strategy starts by understanding ",
    p4b: "people, not algorithms",
    caption1: "✷ new york · 2025",
    photoAlt: "Gabriela Ferraz in New York",
    services: [
      {
        t: "strategy",
        items: ["Creative strategy", "Content strategy", "Positioning & tone of voice", "Editorial planning"],
      },
      { t: "copy", items: ["Copywriting", "Storytelling", "Scripts", "Brand manifesto"] },
      { t: "social", items: ["Community management", "Influencer management", "Real-time coverage"] },
      { t: "editorial", items: ["Pitching & reporting", "Content editing", "Podcasts", "Reels & TikTok"] },
      { t: "campaigns", items: ["Creative concept", "Launch", "Digital activation"] },
    ],
  },
};

export default function About() {
  const t = useT(T);
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-40"
    >
      <div className="container-wide">
        <div className="flex items-center gap-3 mb-10">
          <span className="label">{t.label}</span>
          <span className="text-2xl">☺</span>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-16 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-8 font-display uppercase text-[13vw] md:text-[7vw]"
            data-testid="about-heading"
          >
            {t.heading1}<br />
            <span className="text-brick">{t.heading2}</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-5"
            data-testid="about-photo-wrap"
          >
            <div className="border border-[color:var(--ink)] bg-cloud p-2">
              <div className="relative w-full overflow-hidden bg-cloud" style={{ aspectRatio: "3 / 4" }}>
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_meme-pro-design/artifacts/nvgs0vpz_IMG_5220.webp"
                  alt={t.photoAlt}
                  data-testid="about-photo"
                  className="absolute"
                  style={{
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(90deg)",
                    width: "auto",
                    height: "100%",
                    maxWidth: "none",
                    filter: "grayscale(1) contrast(1.05)",
                    transformOrigin: "center center",
                    objectPosition: "center",
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 px-1 label">
                <span>{t.caption1}</span>
                <span>#gf.002</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-7"
          >
            <p className="font-body text-lg md:text-xl leading-relaxed">
              {t.p1a}
              <span className="font-semibold">{t.p1b}</span>
              {t.p1c}
            </p>
            <p className="mt-4 font-display uppercase text-2xl md:text-3xl leading-[1.05] text-brick">
              {t.pManifesto[0]}<br />
              {t.pManifesto[1]}
            </p>
            <p className="mt-6 font-body text-lg md:text-xl leading-relaxed">
              {t.p2}
            </p>
            <p className="mt-6 font-body text-lg md:text-xl leading-relaxed">
              {t.p3}
            </p>
            <p className="mt-6 font-body text-lg md:text-xl leading-relaxed">
              {t.p4a}
              <span className="text-brick font-semibold">{t.p4b}</span>.
            </p>

            <div className="mt-12" data-testid="services-list">
              <div className="border-t border-[color:var(--ink)]">
                {t.services.map((s, i) => (
                  <div
                    key={s.t}
                    className="grid grid-cols-12 gap-4 items-start py-6 border-b border-[color:var(--ink)]"
                    data-testid={`service-${String(i + 1).padStart(2, "0")}`}
                  >
                    <div className="col-span-12 md:col-span-4 flex items-start justify-between md:justify-start gap-3">
                      <span className="font-display uppercase text-xl md:text-2xl">{s.t}</span>
                      <span className="font-narrow text-brick text-sm">0{i + 1}</span>
                    </div>
                    <ul className="col-span-12 md:col-span-8 flex flex-wrap gap-x-4 gap-y-1 font-body text-sm md:text-base text-[color:var(--ink)]/80">
                      {s.items.map((it, j) => (
                        <li
                          key={it}
                          className="whitespace-nowrap"
                          data-testid={`service-${String(i + 1).padStart(2, "0")}-item-${j}`}
                        >
                          {it}
                          {j < s.items.length - 1 && (
                            <span className="ml-4 text-brick/70">·</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
